// @flow
/**
 * JS oznámení o použití cookies na webu a potvrzení plnoletosti
 */

import './scss/index.scss';
import arrowImg from './img/arrow.svg';

// eslint-disable-next-line no-unused-vars
const VERSION = '1.0.0';

type UnifiedCookiePolicyLang = 'cs'

type UnifiedCookiePolicyProps = {
  activeSettingsClassName?: string,
  container?: string,
  cookieLifetime?: number,
  cookieName?: string,
  preventPageScrollClassName: string,
  enableOneGTMContainer?: boolean,
  enableRejectButton?: boolean,
  enableSplitGTMContainer?: boolean,
  excludedCPClassName?: string,
  excludedSelectors?: Array,
  gtmCodeTemplate: (Object) => string,
  hiddenClassName?: string,
  logoImgSrc: string,
  logoImgAlt: string,
  motiveImgSrc?: string,
  arrowImg: string,
  lang: UnifiedCookiePolicyLang,
  messagesSingle?: Object,
  messagesMulti?: Object,
  settingsMessages?: Object,
  position?: "left" | "right" | "top" | "bottom" | "center",
  type?: "single" | "multi",
  color?: "green" | "black",
  settingsTemplate: (Object) => string,
  templateMulti: (Object) => string,
  templateSingle: (Object, Object) => string,
}

type UnifiedCookiePolicyObjects = {
  acceptAllButtons: NodeList,
  acceptSelectedButton: HTMLButtonElement,
  rejectButton: HTMLButtonElement,
  openSettingsButton: HTMLButtonElement,
  openSettingsLinks: NodeList,
  pane: HTMLElement,
  settings: HTMLElement,
  settingsCheckboxAnalytics: HTMLInputElement,
  settingsCheckboxAnalyticsMarketing: HTMLInputElement,
  settingsCheckboxMarketing: HTMLInputElement,
  settingsStateText: HTMLElement,
  settingsAccordions: HTMLElement[],
  isExcluded: boolean,
}

const FRAGMENTS_CONTENT = {
  SETTINGS_ACCORDION: 'cp.accordion',
}

const FRAGMENTS = {
  ACCEPT_ALL_BTN:                        '[data-fragment="cp.acceptAllBtn"]',
  ACCEPT_SELECTED_BTN:                   '[data-fragment="cp.acceptSelectedButton"]',
  REJECT_BTN:                            '[data-fragment="cp.rejectButton"]',
  OPEN_SETTINGS_BTN:                     '[data-fragment="cp.openSettingsButton"]',
  OPEN_SETTINGS_LINK:                    '[data-fragment="cp.openSettingsLink"]', // link in websites to open settings menu
  PANE:                                  '[data-fragment="cp.pane"]',
  SETTINGS:                              '[data-fragment="cp.settings"]',
  SETTINGS_CHECKBOX_ANALYTICS:           '[data-fragment="cp.checkboxAnalytics"]',
  SETTINGS_CHECKBOX_ANALYTICS_MARKETING: '[data-fragment="cp.checkboxAnalyticsMarketing"]',
  SETTINGS_CHECKBOX_MARKETING:           '[data-fragment="cp.checkboxMarketing"]',
  SETTINGS_STATE_TEXT:                   '[data-fragment="cp.settingsStateText"]',
  SETTINGS_ACCORDION:                    `[data-fragment="cp.accordion"]`,
  SETTINGS_ACCORDION_HEADER:             '[data-fragment="cp.accordionHeader"]',
  SETTINGS_ACCORDION_BODY:               '[data-fragment="cp.accordionBody"]'
};

const LEVEL = {
  INIT:                -1, // novy uzivatel, panel nevidel
  BASE:                0,  // uzivatel videl panel a nastaveni ignoruje
  CONFIG:              1,  // uzivatel videl panel a chce nastavit uroven
  TECHNICAL:           2,  // uz. souhlasi s technickymi cookies (min uroven po ulozeni nastaveni)
  ANALYTICS:           3,  // uz. souhlasi s technickymi A analytickymi cookies (nepouzito)
  MARKETING:           4,  // uz. souhlasi s technickymi A marketingovymi cookies
  ANALYTICS_MARKETING: 5,  // uz. souhlasi s technickymi A analytickymi A marketingovymi cookies
};

const COLORS = {
  green: {
    base: "#54A642",
    hover: "#3d802e"
  },
  black: {
    base: "#000",
    hover: "#2b2b2b"
  }
}

/**
 * Deep merge of multiple Objects
 *
 * Examples:
 * obj = merge({x: {a: 2}}, {x: {b: 3}})                => { x: { a: 2, b: 3 } }
 * obj = merge({x: {a: 2}}, {x: {b: 3}}, {x: {a: 1}})   => { x: { a: 1, b: 3 } }

 * @return {{}}
 */
const merge = function () {
  let ret = {},
    i   = 0,
    il  = arguments.length,
    p;
  for ( ; i < il; i++ ) {
    for ( p in arguments[ i ] ) {
      try {
        if ( arguments[ i ][ p ].constructor == Object ) {
          ret[ p ] = merge(ret[ p ], arguments[ i ][ p ]);
        } else {
          ret[ p ] = arguments[ i ][ p ];
        }
      } catch ( e ) {
        ret[ p ] = arguments[ i ][ p ];
      }
    }
  }
  return ret;
};

window.dataLayer = window.dataLayer || [];
const gtag = function () {
  window.dataLayer.push(arguments);
};

export class UnifiedCookiePolicy {

  defaultProps: UnifiedCookiePolicyProps;
  props: UnifiedCookiePolicyProps;
  element: HTMLElement;
  objects: Array<UnifiedCookiePolicyObjects>;

  static defaultProps = {
    activeSettingsClassName: 'cookies-settings--active',
    preventPageScrollClassName: 'prevent-scroll',
    enableOneGTMContainer:   false,
    enableRejectButton:      false,
    templateSingle:          (data, settingsData) => `
      <section class="cookies-policy cookies-policy--single cookies-policy--${ data.position }" style="--cookies-accent-color: ${data.colors.base}; --cookies-accent-color-hover: ${data.colors.hover}" data-fragment="cp.pane">
        <div class="cookies-policy__modal">
          <div class="cookies-policy__logo-header">
            <div class="cookies-policy__logo-container">
              <img class="cookies-policy__logo" src="${data.logoImgSrc}" alt="${data.logoImgAlt}" />
              ${
                !data.motiveImgSrc === '' ? '' : `
                  <img class="cookies-policy__motive" src="${ data.motiveImgSrc }" alt="" />
                `
              }
            </div>
          </div>
          <div class="cookies-policy__container">
            <div class="cookies-policy__header">
              <h2 class="cookies-policy__heading">${ data.heading }</h2>
            </div>
            <div class="cookies-policy__main">
              <p class="cookies-policy__perex">${ data.perex }</p>
              <div class="cookies-policy__settings">
                <h2 class="cookies-settings__title">${ data.settingsTitle }</h2>
                <section class="cookies-settings__section" data-fragment="cp.accordion">
                  <header class="cookies-settings__section-header">
                    <button class="cookies-settings__section-title-container" data-fragment="cp.accordionHeader" role="button" tabindex="0">
                      <img class="cookies-settings__section-icon" src="${ settingsData.arrowImg }" alt=""/>
                      <h3 class="cookies-settings__section-title">${ settingsData.technicalTitle }</h3>
                    </button>
                    <div class="cookies-settings__section-state-container">
                      <input id="technical-cookies" class="cookies-settings__checkbox" type="checkbox" name="technicalCookies" value="technicalCookies" checked disabled>
                      <label class="cookies-settings__label cookies-settings__section-state" for="technical-cookies" tabindex="0">
                        <span class="cookies-settings__section-state-text">${ settingsData.technicalState }</span>
                        <span class="cookies-settings__switch"></span>
                      </label>
                    </div>
                  </header>
                  <div class="cookies-settings__section-main" data-fragment="cp.accordionBody">
                    <p class="cookies-settings__section-perex">${ settingsData.technicalPerex }</p>
                  </div>
                </section>
                ${ settingsData.enableSplitGTMContainer ? `
                <section class="cookies-settings__section" data-fragment="cp.accordion">
                   <header class="cookies-settings__section-header">
                     <button class="cookies-settings__section-title-container" data-fragment="cp.accordionHeader" role="button" tabindex="0">
                       <img class="cookies-settings__section-icon" src="${ settingsData.arrowImg }" alt=""/>
                       <h3 class="cookies-settings__section-title">${ settingsData.analyticsTitle }</h3>
                     </button>
                     <div class="cookies-settings__section-state-container">
                       <input id="analytics-cookies" class="cookies-settings__checkbox" type="checkbox" name="analyticsCookies" value="analyticsCookies" data-fragment="cp.checkboxAnalytics">
                       <label class="cookies-settings__label cookies-settings__section-state" for="analytics-cookies" tabindex="0">
                         <span class="cookies-settings__section-state-text" data-fragment="cp.settingsStateText">${ settingsData.checkboxStateOff }</span>
                         <span class="cookies-settings__switch"></span>
                       </label>
                     </div>
                   </header>
                   <div class="cookies-settings__section-main" data-fragment="cp.accordionBody">
                     <p class="cookies-settings__section-perex">${ settingsData.analyticsPerex }</p>
                   </div>
                </section>
                <section class="cookies-settings__section" data-fragment="cp.accordion">
                  <header class="cookies-settings__section-header">
                    <button class="cookies-settings__section-title-container" data-fragment="cp.accordionHeader" role="button" tabindex="0">
                      <img class="cookies-settings__section-icon" src="${ settingsData.arrowImg }" alt=""/>
                      <h3 class="cookies-settings__section-title">${ settingsData.marketingTitle }</h3>
                    </button>
                    <div class="cookies-settings__section-state-container">
                      <input id="marketing-cookies" class="cookies-settings__checkbox" type="checkbox" name="marketingCookies" value="marketingCookies" data-fragment="cp.checkboxMarketing">
                      <label class="cookies-settings__label cookies-settings__section-state" for="marketing-cookies" tabindex="0">
                        <span class="cookies-settings__section-state-text" data-fragment="cp.settingsStateText">${ settingsData.checkboxStateOff }</span>
                        <span class="cookies-settings__switch"></span>
                      </label>
                    </div>
                  </header>
                  <div class="cookies-settings__section-main" data-fragment="cp.accordionBody">
                    <p class="cookies-settings__section-perex">${ settingsData.marketingPerex }</p>
                  </div>
                </section>` : `
                <section class="cookies-settings__section cookies-settings__section--analytics-marketing" data-fragment="cp.accordion">
                  <header class="cookies-settings__section-header">
                    <button class="cookies-settings__section-title-container" data-fragment="cp.accordionHeader" role="button" tabindex="0">
                      <img class="cookies-settings__section-icon" src="${ settingsData.arrowImg }" alt=""/>
                      <h3 class="cookies-settings__section-title">${ settingsData.analyticsMarketingTitle }</h3>
                    </button>
                    <div class="cookies-settings__section-state-container">
                      <input id="analytics-marketing-cookies" class="cookies-settings__checkbox" type="checkbox" name="analyticsMarketingCookies" value="analyticsMarketingCookies" data-fragment="cp.checkboxAnalyticsMarketing">
                      <label class="cookies-settings__label cookies-settings__section-state" for="analytics-marketing-cookies" tabindex="0">
                        <span class="cookies-settings__section-state-text" data-fragment="cp.settingsStateText">${ settingsData.checkboxStateOff }</span>
                        <span class="cookies-settings__switch"></span>
                      </label>
                    </div>
                  </header>
                  <div class="cookies-settings__section-main" data-fragment="cp.accordionBody">
                    <h4 class="cookies-settings__section-sub-title">${ settingsData.analyticsTitle }</h4>
                    <p class="cookies-settings__section-perex">${ settingsData.analyticsPerex }</p>
                  </div>
                  <div class="cookies-settings__section-main" data-fragment="cp.accordionBody">
                    <h4 class="cookies-settings__section-sub-title">${ settingsData.marketingTitle }</h4>
                    <p class="cookies-settings__section-perex">${ settingsData.marketingPerex }</p>
                  </div>
                </section>`}
              </div>
              <button class="cookies-policy__accept-selected-btn cookies-policy__btn cookies-policy__btn--secondary" data-fragment="cp.acceptSelectedButton">${ data.acceptSelectedBtn }</button>
              <button class="cookies-policy__accept-all-btn cookies-policy__btn cookies-policy__btn--primary" data-fragment="cp.acceptAllBtn">${ data.acceptAllBtn }</button>
            </div>
          </div>
        </div>
      </section>
      `,
    enableSplitGTMContainer: false,
    excludedSelectors:       [],
    hiddenClassName:         'cookies-policy--hidden',
    templateMulti:                data => `
      <section class="cookies-policy cookies-policy--multi cookies-policy--${ data.position }" style="--cookies-accent-color: ${data.colors.base}; --cookies-accent-color-hover: ${data.colors.hover}" data-fragment="cp.pane">
        <div class="cookies-policy__modal">
          <div class="cookies-policy__logo-header">
            <div class="cookies-policy__logo-container">
              <img class="cookies-policy__logo" src="${ data.logoImgSrc }" alt="${ data.logoImgAlt }" />
              ${
                !data.motiveImgSrc === '' ? '' : `
                  <img class="cookies-policy__motive" src="${ data.motiveImgSrc }" alt="" />
                `
              }
            </div>
          </div>
          <div class="cookies-policy__container">
            <div class="cookies-policy__header">
              <h2 class="cookies-policy__heading">${ data.heading }</h2>
            </div>
            <div class="cookies-policy__main">
              <p class="cookies-policy__perex">${ data.perex }</p>
            </div>
            <div class="cookies-policy__footer">
              <button class="cookies-policy__btn cookies-policy__btn--primary" data-fragment="cp.acceptAllBtn">${ data.acceptAllBtn }</button>
              <button class="cookies-policy__btn cookies-policy__btn--secondary" data-fragment="cp.openSettingsButton">${ data.settingsBtn }</button>
            </div>
          </div>
        </div>
      </section>`,
    messagesSingle:                {
      cs: {
        heading:      'Pokud Vám je více než 18 let, pokračujte nastavením cookies',
        perex:        `Abychom mohli web neustále zlepšovat a poskytovat Vám tu nejvhodnější nabídku produktů, potřebujeme používat soubory cookies. Poskytnuté informace jsou u&nbsp;nás v&nbsp;naprostém bezpečí. <a class="cookies-policy__link" href="/cs/informace-o-cookies">Více&nbsp;informací o&nbsp;cookies</a>.`,
        acceptAllBtn:            'Bylo mi 18 let a přijímám všechny cookies',
        acceptSelectedBtn:       'Bylo mi 18 let a přijímám vybrané cookies',
        checkboxStateOff:        'Vypnuto',
        checkboxStateOn:         'Zapnuto',
        closeBtn:                'Zavřít',
        settingsBtn:             'Podrobné nastavení',
        settingsTitle:           'Vyberu si cookies podle sebe',
      },
    },
    settingsTemplate:        settingsData => `
      <section class="cookies-policy cookies-policy--settings cookies-policy--${ settingsData.position } ${ settingsData.hiddenClassName }" style="--cookies-accent-color: ${settingsData.colors.base}; --cookies-accent-color-hover: ${settingsData.colors.hover}" data-fragment="cp.settings">
        <div class="cookies-policy__modal">
          <div class="cookies-policy__logo-header">
            <div class="cookies-policy__logo-container">
              <img class="cookies-policy__logo" src="${ settingsData.logoImgSrc }" alt="${ settingsData.logoImgAlt }" />
              ${
                !settingsData.motiveImgSrc === '' ? '' : `
                  <img class="cookies-policy__motive" src="${ settingsData.motiveImgSrc }" alt="" />
                `
              }
            </div>
          </div>
          <div class="cookies-policy__container">
            <div class="cookies-policy__header">
              <h2 class="cookies-policy__heading">${ settingsData.heading }</h2>
          </div>
          <div class="cookies-policy__main">
            <p class="cookies-policy__perex">${ settingsData.perex }</p>
            <section class="cookies-settings__section" data-fragment="cp.accordion">
              <header class="cookies-settings__section-header">
                <button class="cookies-settings__section-title-container" data-fragment="cp.accordionHeader" role="button" tabindex="0">
                  <img class="cookies-settings__section-icon" src="${ settingsData.arrowImg }" alt=""/>
                  <h3 class="cookies-settings__section-title">${ settingsData.technicalTitle }</h3>
                </button>
                <div class="cookies-settings__section-state-container">
                  <input id="technical-cookies" class="cookies-settings__checkbox" type="checkbox" name="technicalCookies" value="technicalCookies" checked disabled>
                  <label class="cookies-settings__label cookies-settings__section-state" for="technical-cookies" tabindex="0">
                    <span class="cookies-settings__section-state-text">${ settingsData.technicalState }</span>
                    <span class="cookies-settings__switch"></span>
                  </label>
                </div>
              </header>
              <div class="cookies-settings__section-main" data-fragment="cp.accordionBody">
                <p class="cookies-settings__section-perex">${ settingsData.technicalPerex }</p>
              </div>
            </section>
            ${ settingsData.enableSplitGTMContainer ? `
            <section class="cookies-settings__section" data-fragment="cp.accordion">
               <header class="cookies-settings__section-header">
                 <button class="cookies-settings__section-title-container" data-fragment="cp.accordionHeader" role="button" tabindex="0">
                   <img class="cookies-settings__section-icon" src="${ settingsData.arrowImg }" alt=""/>
                   <h3 class="cookies-settings__section-title">${ settingsData.analyticsTitle }</h3>
                 </button>
                 <div class="cookies-settings__section-state-container">
                   <input id="analytics-cookies" class="cookies-settings__checkbox" type="checkbox" name="analyticsCookies" value="analyticsCookies" data-fragment="cp.checkboxAnalytics">
                   <label class="cookies-settings__label cookies-settings__section-state" for="analytics-cookies" tabindex="0">
                     <span class="cookies-settings__section-state-text" data-fragment="cp.settingsStateText">${ settingsData.checkboxStateOff }</span>
                     <span class="cookies-settings__switch"></span>
                   </label>
                 </div>
               </header>
               <div class="cookies-settings__section-main" data-fragment="cp.accordionBody">
                 <p class="cookies-settings__section-perex">${ settingsData.analyticsPerex }</p>
               </div>
            </section>
            <section class="cookies-settings__section" data-fragment="cp.accordion">
              <header class="cookies-settings__section-header">
                <button class="cookies-settings__section-title-container" data-fragment="cp.accordionHeader" role="button" tabindex="0">
                  <img class="cookies-settings__section-icon" src="${ settingsData.arrowImg }" alt=""/>
                  <h3 class="cookies-settings__section-title">${ settingsData.marketingTitle }</h3>
                </button>
                <div class="cookies-settings__section-state-container">
                  <input id="marketing-cookies" class="cookies-settings__checkbox" type="checkbox" name="marketingCookies" value="marketingCookies" data-fragment="cp.checkboxMarketing">
                  <label class="cookies-settings__label cookies-settings__section-state" for="marketing-cookies" tabindex="0">
                    <span class="cookies-settings__section-state-text" data-fragment="cp.settingsStateText">${ settingsData.checkboxStateOff }</span>
                    <span class="cookies-settings__switch"></span>
                  </label>
                </div>
              </header>
              <div class="cookies-settings__section-main" data-fragment="cp.accordionBody">
                <p class="cookies-settings__section-perex">${ settingsData.marketingPerex }</p>
              </div>
            </section>` : `
            <section class="cookies-settings__section cookies-settings__section--analytics-marketing" data-fragment="cp.accordion">
              <header class="cookies-settings__section-header">
                <button class="cookies-settings__section-title-container" data-fragment="cp.accordionHeader" role="button" tabindex="0">
                  <img class="cookies-settings__section-icon" src="${ settingsData.arrowImg }" alt=""/>
                  <h3 class="cookies-settings__section-title">${ settingsData.analyticsMarketingTitle }</h3>
                </button>
                <div class="cookies-settings__section-state-container">
                  <input id="analytics-marketing-cookies" class="cookies-settings__checkbox" type="checkbox" name="analyticsMarketingCookies" value="analyticsMarketingCookies" data-fragment="cp.checkboxAnalyticsMarketing">
                  <label class="cookies-settings__label cookies-settings__section-state" for="analytics-marketing-cookies" tabindex="0">
                    <span class="cookies-settings__section-state-text" data-fragment="cp.settingsStateText">${ settingsData.checkboxStateOff }</span>
                    <span class="cookies-settings__switch"></span>
                  </label>
                </div>
              </header>
              <div class="cookies-settings__section-main" data-fragment="cp.accordionBody">
                <h4 class="cookies-settings__section-sub-title">${ settingsData.analyticsTitle }</h4>
                <p class="cookies-settings__section-perex">${ settingsData.analyticsPerex }</p>
              </div>
              <div class="cookies-settings__section-main" data-fragment="cp.accordionBody">
                <h4 class="cookies-settings__section-sub-title">${ settingsData.marketingTitle }</h4>
                <p class="cookies-settings__section-perex">${ settingsData.marketingPerex }</p>
              </div>
            </section>` }
          </div>
          <div class="cookies-policy__footer">
            <button class="cookies-policy__btn cookies-policy__btn--primary" data-fragment="cp.acceptAllBtn">${ settingsData.acceptAllBtn }</button>
            <button class="cookies-policy__btn cookies-policy__btn--secondary" data-fragment="cp.acceptSelectedButton">${ settingsData.acceptSelectedBtn }</button>
          </div>
        </div>
      </section>`,
    messagesMulti:                {
      cs: {
        heading:      'Pokud Vám je více než 18 let, pokračujte nastavením cookies',
        perex:        `Abychom mohli web neustále zlepšovat a poskytovat Vám tu nejvhodnější nabídku produktů,
                       potřebujeme používat soubory cookies. Zletilost potvrzujete, i&nbsp;v&nbsp;případě, že se rozhodnete
                       volitelné cookies <button class="cookies-policy__link" data-fragment="cp.rejectButton" href="#" role="button" tabindex="0">odmítnout</button>.<br/><br/>
                       Poskytnuté informace jsou u&nbsp;nás v&nbsp;naprostém bezpečí. <a class="cookies-policy__link" href="/cs/informace-o-cookies">Více&nbsp;informací o&nbsp;cookies</a>.`,
        closeBtn:     'Zavřít',
        settingsBtn:  'Podrobné nastavení',
        rejectBtn:    'Odmítnout',
        acceptAllBtn: 'Bylo mi 18 let a přijímám všechny cookies',
      },
    },
    settingsMessages:        {
      cs: {
        heading:                 'Nastavení cookies',
        perex:                   'Poskytnuté informace jsou u&nbsp;nás v&nbsp;naprostém bezpečí. <a class="cookies-settings__link" href="/cs/informace-o-cookies">Více&nbsp;informací o&nbsp;cookies.</a>',
        acceptAllBtn:            'Bylo mi 18 let a přijímám všechny cookies',
        acceptSelectedBtn:       'Bylo mi 18 let a přijímám vybrané cookies',
        checkboxStateOff:        'Vypnuto',
        checkboxStateOn:         'Zapnuto',
        closeBtn:                'Zavřít',
        technicalTitle:          'Technické cookies',
        technicalPerex:          'Jsou nutné k&nbsp;tomu, abychom vám&nbsp;zobrazili naše webové stránky a&nbsp;fungovaly vám&nbsp;tak, jak&nbsp;mají. Tyto cookies jsou&nbsp;povinné a&nbsp;nelze je&nbsp;vypnout.',
        technicalState:          'Vždy zapnuto',
        analyticsMarketingTitle: 'Analytické a&nbsp;marketingové cookies',
        analyticsTitle:          'Analytické cookies',
        analyticsPerex:          'Pomáhají analyzovat, jak&nbsp;fungují naše webové stránky z&nbsp;hlediska chování návštěvníků a&nbsp;podle toho web&nbsp;případně přizpůsobovat a&nbsp;měnit.',
        marketingTitle:          'Marketingové cookies',
        marketingPerex:          'Díky těmto cookies můžeme my&nbsp;nebo třetí strany po&nbsp;vaší návštěvě přizpůsobit nabídku našich služeb. Tato nabídka se&nbsp;vám&nbsp;potom může zobrazovat i&nbsp;mimo naše webové stránky.',
      },
    },

    // no need to edit, there are always default values
    container:           'body',
    cookieName:          'fg_cookie_policy_accepted',
    cookieLifetime:      365,
    excludedCPClassName: 'js_hideCookiePolicy',
    gtmCodeTemplate:     gtmId => `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
'//www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
})(window,document,'script','dataLayer','${ gtmId }');`,
    lang:                'cs',
    position:            'bottom',
    type:                'single',
    color:               'green',
    arrowImg:            arrowImg
  };

  constructor(props?: UnifiedCookiePolicyProps) {

    if ( !navigator.cookieEnabled ) {
      return false;
    }

    this.state = {
      accepted: LEVEL.INIT,
    };

    this.props = this.constructor.defaultProps;
    this.props = merge(this.props, props);

    this.element = document.querySelector(this.props.container);

    this.objects = {};

    this.objects.isExcluded = false;
    if ( this.props.excludedSelectors.length > 0 ) {
      this.isExcluded();
      if ( this.objects.isExcluded ) {
        return false;
      }
    }

    // objects
    this.objects.acceptAllButtons = null;
    this.objects.acceptSelectedButton = null;
    this.objects.rejectButton = null;
    this.objects.openSettingsButton = null;
    this.objects.openSettingsLinks = null;
    this.objects.pane = null;
    this.objects.settings = null;
    this.objects.settingsCheckboxAnalytics = null;
    this.objects.settingsCheckboxAnalyticsMarketing = null;
    this.objects.settingsCheckboxMarketing = null;
    this.objects.settingsStateText = null;

    this.handleAcceptSelectedClick = this.handleAcceptSelectedClick.bind(this);
    this.handleChangeCheckboxState = this.handleChangeCheckboxState.bind(this);
    this.handleCloseSettingsClick = this.handleCloseSettingsClick.bind(this);
    this.handleSubmitLevel = this.handleSubmitLevel.bind(this);
    this.handleOpenSettingsClick = this.handleOpenSettingsClick.bind(this);
    this.handleClosePolicyClick = this.handleClosePolicyClick.bind(this);

    this.init();
  }

  init(): void {
    this.checkAcceptedCookie();
    // this.pushActiveStateToGTM(); // neposilat aktivni nastaveni CP, pouze pri zmene urovne souhlasu
    this.renderNewGtmContainer();

    /**
     * pokud jsem nedal souhlas se zpracovanim cookies a nenacházím na stránce informace o cookies, vyrenderuj panel
     */
    if ( this.state.accepted <= LEVEL.CONFIG && !(this.element.classList.contains(this.props.excludedCPClassName)) ) {
      this.renderPane();

      this.objects.pane = this.element.querySelector(FRAGMENTS.PANE);
      this.objects.rejectButton = this.element.querySelector(FRAGMENTS.REJECT_BTN);
      this.objects.openSettingsButton = this.element.querySelector(FRAGMENTS.OPEN_SETTINGS_BTN);

      this.bindPaneEvents();

      if (this.props.type === "single") {
        this.bindSettingsObjectsAndEvents()
      }
    }

    /**
     * pokud jsem na stránce, kde nerenderuju cookie lištu, tak odeber excludedCPClassName
     */
    if ( this.element.classList.contains(this.props.excludedCPClassName) ) {
      this.element.classList.remove(this.props.excludedCPClassName);
    }

    /**
     * vyrenderuj settings vždy, když bude vyrenderovaný obsah panelu (cookiePolicy panel)
     * nebo když na stránce najdeš fragment na nastavení-cookies
     */
    if ( (this.objects.openSettingsButton instanceof HTMLElement) || ((this.element.querySelectorAll(FRAGMENTS.OPEN_SETTINGS_LINK) instanceof NodeList) && (this.element.querySelectorAll(FRAGMENTS.OPEN_SETTINGS_LINK).length > 0)) ) {
      this.renderSettings();
      this.bindSettingsObjectsAndEvents();
    }
  }

  setState(newState: Object): void {
    this.state = {
      ...this.state,
      ...newState,
    };
  }

  // eslint-disable-next-line class-methods-use-this,no-unused-vars
  update(prevState?: Object): void {
  }

  renderPane(): void {
    const pane = this.buildPaneCode();
    this.element.insertAdjacentHTML('afterbegin', pane);
  }

  renderSettings(): void {
    const settings = this.buildSettingsCode();
    this.element.insertAdjacentHTML('afterbegin', settings);
  }

  bindPaneEvents(): void {
    // Event listener na tlačítko `Povolit všechny cookie` je až uvnitř funkce `bindSettingsEvents`

    if ( this.objects.closeButton instanceof HTMLButtonElement ) {
      this.objects.closeButton.addEventListener('click', this.handleClosePolicyClick);
    }

    if ( this.objects.openSettingsButton instanceof HTMLButtonElement ) {
      this.objects.openSettingsButton.addEventListener('click', this.handleOpenSettingsClick);
    }

    if ( this.objects.rejectButton instanceof HTMLButtonElement ) {
      this.objects.rejectButton.addEventListener('click', this.handleSubmitLevel(LEVEL.TECHNICAL));
    }
  }

  bindSettingsEvents(): void {
    if ( this.objects.acceptAllButtons instanceof NodeList ) {
      this.objects.acceptAllButtons.forEach((acceptAllBtn) => {
        acceptAllBtn.addEventListener('click', this.handleSubmitLevel(LEVEL.ANALYTICS_MARKETING));
      });
    }

    if ( this.objects.openSettingsLinks instanceof NodeList ) {
      this.objects.openSettingsLinks.forEach((openSettingsLink) => {
        openSettingsLink.addEventListener('click', this.handleOpenSettingsClick);
      });
    }

    if ( this.objects.acceptSelectedButton instanceof HTMLButtonElement ) {
      this.objects.acceptSelectedButton.addEventListener('click', this.handleAcceptSelectedClick);
    }

    if ( this.objects.settingsCheckboxAnalytics instanceof HTMLInputElement ) {
      this.objects.settingsCheckboxAnalytics.addEventListener('click', this.handleChangeCheckboxState);
    }

    if ( this.objects.settingsCheckboxMarketing instanceof HTMLInputElement ) {
      this.objects.settingsCheckboxMarketing.addEventListener('click', this.handleChangeCheckboxState);
    }

    if ( this.objects.settingsCheckboxAnalyticsMarketing instanceof HTMLInputElement ) {
      this.objects.settingsCheckboxAnalyticsMarketing.addEventListener('click', this.handleChangeCheckboxState);
    }

    for (let element of this.objects.settingsAccordionHeaders) {
      element.addEventListener('click', this.handleAccordionClick)
    }
  }

  bindSettingsObjectsAndEvents(): void {
    this.objects.acceptAllButtons = this.element.querySelectorAll(FRAGMENTS.ACCEPT_ALL_BTN);
    this.objects.acceptSelectedButton = this.element.querySelector(FRAGMENTS.ACCEPT_SELECTED_BTN);
    this.objects.openSettingsLinks = this.element.querySelectorAll(FRAGMENTS.OPEN_SETTINGS_LINK);
    this.objects.settings = this.element.querySelector(FRAGMENTS.SETTINGS);
    this.objects.settingsCheckboxAnalytics = this.element.querySelector(FRAGMENTS.SETTINGS_CHECKBOX_ANALYTICS);
    this.objects.settingsCheckboxAnalyticsMarketing = this.element.querySelector(FRAGMENTS.SETTINGS_CHECKBOX_ANALYTICS_MARKETING);
    this.objects.settingsCheckboxMarketing = this.element.querySelector(FRAGMENTS.SETTINGS_CHECKBOX_MARKETING);
    this.objects.settingsStateText = this.element.querySelector(FRAGMENTS.SETTINGS_STATE_TEXT);
    this.objects.settingsAccordions = this.element.querySelectorAll(FRAGMENTS.SETTINGS_ACCORDION)
    this.objects.settingsAccordionHeaders = this.element.querySelectorAll(FRAGMENTS.SETTINGS_ACCORDION_HEADER)

    for (let element of this.objects.settingsAccordions) {
      const content = element.querySelector(FRAGMENTS.SETTINGS_ACCORDION_BODY)

      if (content) {
        element.style.setProperty('--cookies-settings-section-max-height',  content.scrollHeight + 'px')
      }
    }

    this.bindSettingsEvents();
    this.loadCheckboxState();
  }

  checkAcceptedCookie(): void {
    const actualLevel = this.readCookie(this.props.cookieName);
    this.setState({
      accepted: actualLevel,
    });
  }

  loadCheckboxState(): void {
    const { accepted } = this.state;
    if ( (this.objects.settingsCheckboxAnalytics instanceof HTMLInputElement) && (accepted === LEVEL.ANALYTICS || accepted === LEVEL.ANALYTICS_MARKETING) ) {
      this.setCheckboxState(this.objects.settingsCheckboxAnalytics);
    }
    if ( (this.objects.settingsCheckboxMarketing instanceof HTMLInputElement) && (accepted === LEVEL.MARKETING || accepted === LEVEL.ANALYTICS_MARKETING) ) {
      this.setCheckboxState(this.objects.settingsCheckboxMarketing);
    }
    if ( (this.objects.settingsCheckboxAnalyticsMarketing instanceof HTMLInputElement) && (accepted === LEVEL.ANALYTICS_MARKETING) ) {
      this.setCheckboxState(this.objects.settingsCheckboxAnalyticsMarketing);
    }
  }

  setCheckboxState(checkboxElement: HTMLInputElement): void {
    const checkboxText = checkboxElement.parentNode.querySelector(FRAGMENTS.SETTINGS_STATE_TEXT);

    checkboxElement.checked = true;
    checkboxText.innerText = this.props.settingsMessages[ this.props.lang ].checkboxStateOn;
  }

  handleChangeCheckboxState(e: SyntheticInputEvent<EventTarget>): void {
    const eventTargetParent = e.target.parentNode;
    const checkboxText = eventTargetParent.querySelector(FRAGMENTS.SETTINGS_STATE_TEXT);

    if ( e.target.checked ) {
      checkboxText.innerText = this.props.settingsMessages[ this.props.lang ].checkboxStateOn;
    } else {
      checkboxText.innerText = this.props.settingsMessages[ this.props.lang ].checkboxStateOff;
    }
  }

  handleSubmitLevel(level: number) {
    // eslint-disable-next-line no-unused-vars
    return (e?: SyntheticEvent<EventTarget>) => {
      // e.preventDefault();
      this.setCookie(this.props.cookieName, level);
      this.pushActiveStateToGTM();
      this.renderNewGtmContainer();
      this.hidePane();
      this.handleCloseSettingsClick();
    };
  }

  handleAcceptSelectedClick(e?: SyntheticEvent): void {
    e.preventDefault();
    let stateAnalytics = false;
    let stateMarketing = false;
    let stateAnalyticsMarketing = false;
    let cookieLevel = LEVEL.TECHNICAL;

    if ( this.objects.settingsCheckboxAnalytics instanceof HTMLInputElement ) {
      stateAnalytics = this.objects.settingsCheckboxAnalytics.checked;
    }
    if ( this.objects.settingsCheckboxMarketing instanceof HTMLInputElement ) {
      stateMarketing = this.objects.settingsCheckboxMarketing.checked;
    }
    if ( this.objects.settingsCheckboxAnalyticsMarketing instanceof HTMLInputElement ) {
      stateAnalyticsMarketing = this.objects.settingsCheckboxAnalyticsMarketing.checked;
    }

    if ( stateAnalytics ) cookieLevel = LEVEL.ANALYTICS;
    if ( stateMarketing ) cookieLevel = LEVEL.MARKETING;
    if ( (stateAnalytics && stateMarketing) || (stateAnalyticsMarketing) ) cookieLevel = LEVEL.ANALYTICS_MARKETING;

    this.handleSubmitLevel(cookieLevel)();
  }

  handleOpenSettingsClick(e?: SyntheticEvent): void {
    e.preventDefault();
    if (this.objects.settings) {
      this.objects.settings.classList.remove(this.props.hiddenClassName);
    }
    if (this.objects.pane) {
      this.objects.pane.classList.add(this.props.hiddenClassName)
    }
    document.body.classList.add(this.props.preventPageScrollClassName)
  }

  handleCloseSettingsClick(): void {
    if (this.objects.settings) {
      this.objects.settings.classList.add(this.props.hiddenClassName);
    }
    else {
      this.handleClosePolicyClick()
    }
    document.body.classList.remove(this.props.preventPageScrollClassName)
  }

  handleClosePolicyClick(): void {
    this.objects.pane.classList.add(this.props.hiddenClassName);
    document.body.classList.remove(this.props.preventPageScrollClassName)
  }

  buildPaneCode(): string {
    document.body.classList.add(this.props.preventPageScrollClassName)
    if (this.props.type === "single") {
      return this.props.templateSingle(
        { // data
          position:           this.props.position,
          colors:             (COLORS.hasOwnProperty(this.props.color))? COLORS[ this.props.color ] : COLORS[ UnifiedCookiePolicy.defaultProps.color ],
          logoImgSrc:         this.props.logoImgSrc,
          logoImgAlt:         this.props.logoImgAlt,
          motiveImgSrc:       this.props.motiveImgSrc,
          enableRejectButton: this.props.enableRejectButton,
          ...this.props.messagesSingle[ this.props.lang ],
        }, {
          enableSplitGTMContainer: this.props.enableSplitGTMContainer,
          arrowImg:           this.props.arrowImg,
          ...this.props.settingsMessages[ this.props.lang ],
        },
      );
    }
    else {
      return this.props.templateMulti({
        position:           this.props.position,
        colors:             (COLORS.hasOwnProperty(this.props.color))? COLORS[ this.props.color ] : COLORS[ UnifiedCookiePolicy.defaultProps.color ],
        logoImgSrc:         this.props.logoImgSrc,
        logoImgAlt:         this.props.logoImgAlt,
        motiveImgSrc:       this.props.motiveImgSrc,
        enableRejectButton: this.props.enableRejectButton,
        ...this.props.messagesMulti[ this.props.lang ],
      });
    }
  }

  buildSettingsCode(): string {
    return this.props.settingsTemplate({
      position:           this.props.position,
      colors:             (COLORS.hasOwnProperty(this.props.color))? COLORS[ this.props.color ] : COLORS[ UnifiedCookiePolicy.defaultProps.color ],
      logoImgSrc:         this.props.logoImgSrc,
      logoImgAlt:         this.props.logoImgAlt,
      arrowImg:           this.props.arrowImg,
      motiveImgSrc:       this.props.motiveImgSrc,
      enableSplitGTMContainer: this.props.enableSplitGTMContainer,
      hiddenClassName:    this.props.hiddenClassName,
      ...this.props.settingsMessages[ this.props.lang ],
    });
  }

  readCookie(cookieName = this.props.cookieName): number {
    const match = document.cookie.match(new RegExp(`${ cookieName }=([^;]+)`));
    if ( match ) return parseInt(match[ 1 ], 10);
    return parseInt(LEVEL.INIT);
  }

  setCookie(cookieName = this.props.cookieName, cookieValue: number): void {
    const { cookieLifetime } = this.props;
    const d = new Date();
    const expires = new Date(d.setTime(d.getTime() + (cookieLifetime * 24 * 60 * 60 * 1000)));
    const lifetime = (cookieLifetime > 0) ? `;expires=${ expires.toUTCString() }` : '';
    const secure = location.protocol === 'https:' ? ';secure' : '';
    document.cookie = `${ cookieName }=${ cookieValue.toString() }${ lifetime };path=/${ secure }`;
  }

  hidePane() {
    if ( this.objects.pane instanceof HTMLElement ) {
      this.objects.pane.classList.add(this.props.hiddenClassName);
    }
    document.body.classList.remove(this.props.preventPageScrollClassName)
  }

  pushActiveStateToGTM() {
    const { accepted } = this.state;
    const cookieLevel = this.readCookie(this.props.cookieName);
    if ( accepted === cookieLevel ) return;

    const level = {
      ad_storage:        (cookieLevel === LEVEL.MARKETING || cookieLevel === LEVEL.ANALYTICS_MARKETING) ? 'granted' : 'denied',
      ad_user_data:        (cookieLevel === LEVEL.MARKETING || cookieLevel === LEVEL.ANALYTICS_MARKETING) ? 'granted' : 'denied',
      ad_personalization:        (cookieLevel === LEVEL.MARKETING || cookieLevel === LEVEL.ANALYTICS_MARKETING) ? 'granted' : 'denied',
      analytics_storage: (cookieLevel === LEVEL.ANALYTICS || cookieLevel === LEVEL.ANALYTICS_MARKETING) ? 'granted' : 'denied',
      functionality_storage:   'granted',
      personalization_storage: 'granted',
      security_storage:        'granted',
    };

    gtag('consent', 'update', level);
    dataLayer.push({
      event:   'consent_update',
      consent: level,
    });

    this.setState({
      accepted: cookieLevel,
    });
  }

  renderNewGtmContainer(): void {
    if ( this.props.enableOneGTMContainer ) return;

    const cookieLevel = this.readCookie(this.props.cookieName);

    /**
     * Analytics kontejner se vyrenderuje pouze, když cookie level je 3 nebo 5
     * a zároveň musí být props `enableSplitGTMContainer` na true
     */
    if ( (cookieLevel === LEVEL.ANALYTICS || cookieLevel === LEVEL.ANALYTICS_MARKETING) && (this.props.enableSplitGTMContainer) ) {
      const gtmId = document.documentElement.dataset.gtmAnalytics;
      if ( gtmId && gtmId !== '!NOTSET!' ) {
        const gtmScript = document.createElement('script');
        gtmScript.text = this.props.gtmCodeTemplate(gtmId);
        document.body.appendChild(gtmScript);
      } else {
        console.error('GTM container for analytics tags is not set!');
      }
    }

    /**
     * Marketing kontejner se vyrenderuje pouze, když cookie level je 4 nebo 5
     */
    if ( cookieLevel === LEVEL.MARKETING || cookieLevel === LEVEL.ANALYTICS_MARKETING ) {
      const gtmId = document.documentElement.dataset.gtmMarketing;
      if ( gtmId && gtmId !== '!NOTSET!' ) {
        const gtmScript = document.createElement('script');
        gtmScript.text = this.props.gtmCodeTemplate(gtmId);
        document.body.appendChild(gtmScript);
      } else {
        console.error('GTM container for marketing tags is not set!');
      }
    }
  }

  isExcluded(): void {
    [ ...this.props.excludedSelectors ].find((excludedSelector) => {
      if ( document.querySelector(excludedSelector) instanceof HTMLElement ) {
        this.objects.isExcluded = true;
      }
      return this.objects.isExcluded;
    });
  }

  handleAccordionClick(event): void {
    let element = event.target

    while(element.dataset.fragment !== FRAGMENTS_CONTENT.SETTINGS_ACCORDION) {
      element = element.parentNode
    }

    document.querySelectorAll(FRAGMENTS.SETTINGS_ACCORDION).forEach((accordion) => {
      if (element !== accordion) {
        accordion.classList.remove('cookies-settings__section--open')
      }
    })

    element.classList.toggle('cookies-settings__section--open')
  }
}

export default (props): void => new UnifiedCookiePolicy({
  lang: window.projectConfig.lang || 'cs',
  ...props,
});



// WEBPACK FOOTER //
// ./fe/web-warehouse/src/js/components/UnifiedCookiesPolicy/UnifiedCookiesPolicy.js